import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  viewChildren,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { ENVIRONMENT } from "../../../../environments/environment";
import { EnergyConsult } from "../../../classes/flow/request/EnergyConsult";
import { RequestStates } from "../../../classes/flow/request/RequestStates";
import { RoleName } from "../../../classes/flow/session/Session";
import { ModuleDirective } from "../../../directives/module-directive";
import { ApplicationService } from "../../../services/application.service";
import { AuthService } from "../../../services/auth.service";
import { FilterGroup } from "../../badge-filters/badge-filters";
import { Header } from "../../table/Header";
import { TableComponent } from "../../table/table.component";

interface SpecialtyValue {
  show: boolean;
  allConsults: EnergyConsult[];
  ownedConsults: EnergyConsult[];
  filteredByStringConsults?: EnergyConsult[];
}

interface StateValue {
  show: boolean;
  value: Record<string, SpecialtyValue>;
}

@Component({
  selector: "app-consult-list-view",
  templateUrl: "./consult-list-view.component.html",
  styleUrls: ["./consult-list-view.component.less"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIcon, TableComponent, MatTooltip, ModuleDirective, TranslatePipe],
})
export class ConsultListViewComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly application = inject(ApplicationService);
  private readonly translate = inject(TranslateService);

  requestTables = viewChildren<TableComponent>("requestTable");

  @ViewChild("noteColumnTemplate") noteColumnTemplate!: TemplateRef<any>;

  private auth = inject(AuthService);

  @Input("consults")
  set valueSetter(consults: EnergyConsult[]) {
    this.setValue(consults);
  }

  @Input("showOtherCoaches") showOtherCoaches!: FormControl<boolean | null>;

  @Input("filters")
  set filtersSetter(filters: FilterGroup<EnergyConsult>[]) {
    this.setShown(filters);
  }

  @Input("filterString")
  set filterStringUpdater(value: string | null) {
    this.filterString = value ?? "";
    this.setFilteredByStringConsults();
  }

  private showOtherCoachesSubscription?: Subscription;

  public energyConsultListHeaders: Header[] = [];

  public role: RoleName | null = null;

  value: {
    [key: string]: StateValue;
  } = {};

  public filterString = "";

  ngOnInit(): void {
    this.role = this.auth.roleName;
    this.showOtherCoachesSubscription = this.showOtherCoaches.valueChanges.subscribe(() => {
      this.setFilteredByStringConsults();
    });
  }

  ngAfterViewInit(): void {
    this.setHeaders();
  }

  ngOnDestroy(): void {
    if (this.showOtherCoachesSubscription) this.showOtherCoachesSubscription.unsubscribe();
  }

  public setValue(consults: EnergyConsult[]) {
    const coachId = this.auth.coachId;
    let states = [...new Set(consults.map((request) => request.state.name))];
    const statesOrder = Object.values(RequestStates);
    states = states.sort((a, b) => statesOrder.indexOf(a) - statesOrder.indexOf(b));
    const specialties = [...new Map(consults.map((request) => [request.specialty.name, request.specialty])).values()];
    for (const state of states) {
      let stateValue = {};
      for (const specialty of specialties) {
        stateValue = {
          [specialty.name]: {
            show: true,
            allConsults: consults.filter((consult) => consult.state.name === state && consult.specialty.name === specialty.name),
            ownedConsults: consults.filter(
              (consult) =>
                consult.state.name === state &&
                consult.specialty.name === specialty.name &&
                (consult.coach?.id === coachId || consult.state.name === RequestStates.NEW),
            ),
          },
          ...stateValue,
        };
      }
      this.value[state] = {
        show: true,
        value: stateValue,
      };
    }
  }

  public getTimeslotDailyMessage(consult: EnergyConsult) {
    let message: string | null = null;
    for (let index = 0; index < (consult.extraProperties?.timeSlotDaily?.length ?? 0); index++) {
      const timeSlot = consult.extraProperties?.timeSlotDaily?.at(index);
      if (!timeSlot) continue;
      if (message === null) message = "";

      if (message !== "" && timeSlot !== "0") {
        message += ", ";
      }
      if (index === 5) {
        if (timeSlot === "1") {
          message += this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.NONE");
        }
      } else {
        if (timeSlot === "1") {
          message += this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.DAYS." + index);
          if (this.translate.currentLang() === "en") message += " ";
          message +=
            this.translate.currentLang() === "en"
              ? this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.MORNING")
              : this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.MORNING").toLowerCase();
        }
        if (timeSlot === "2") {
          message += this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.DAYS." + index);
          if (this.translate.currentLang() === "en") message += " ";
          message +=
            this.translate.currentLang() === "en"
              ? this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.AFTERNOON")
              : this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.AFTERNOON").toLowerCase();
        }
        if (timeSlot === "3") {
          message += `${this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.DAYS." + index)}`;
        }
      }
    }
    return message;
  }

  public getStateKeys(): string[] {
    return Object.keys(this.value);
  }

  public getPerState(state: RequestStates | string) {
    return this.value[state as RequestStates];
  }

  public getSpecialtyKeys(state: RequestStates | string): string[] {
    return Object.keys(this.value[state as RequestStates]?.value ?? {});
  }

  public getPerSpecialtyInState(state: RequestStates | string, specialty: string): SpecialtyValue {
    return (this.value[state as RequestStates]?.value ?? {})[specialty];
  }

  public getConsults(state: RequestStates | string, specialty: string, filtered = true): EnergyConsult[] {
    if (this.filterString && filtered) return (this.value[state as RequestStates]?.value ?? {})[specialty].filteredByStringConsults ?? [];
    return this.showOtherCoaches?.value || this.auth.roleName === "coordinator"
      ? ((this.value[state as RequestStates]?.value ?? {})[specialty].allConsults ?? [])
      : ((this.value[state as RequestStates]?.value ?? {})[specialty].ownedConsults ?? []);
  }

  public isStateEmpty(state: RequestStates | string): boolean {
    for (const specialty of this.getSpecialtyKeys(state)) {
      if (this.getConsults(state, specialty).length > 0 && this.getPerSpecialtyInState(state, specialty).show) {
        return false;
      }
    }
    return true;
  }

  public setShown(filters: FilterGroup<EnergyConsult>[]) {
    for (const filter of filters) {
      switch (filter.name) {
        case "specialty":
          for (const state of this.getStateKeys()) {
            for (const specialty of this.getSpecialtyKeys(state)) {
              this.getPerSpecialtyInState(state, specialty).show = !!filter.activeFilters?.find(
                (filter) => filter.title.split(".").pop() === specialty,
              );
            }
          }
          break;
        case "state":
          for (const state of this.getStateKeys()) {
            this.getPerState(state)!.show = !!filter.activeFilters?.find((filter) => filter.title.split(".").pop() === state);
          }
          break;
        default:
          break;
      }
    }
  }

  public sort(sortMethod: "date" | "lastStateChangeDate", sortAscending: boolean) {
    this.requestTables().forEach((x) => {
      if (x.sort) {
        x.sort.active = "";
        x.sort.direction = "";
        x.sort.sortChange.emit({ active: "", direction: "" });
      }
    });
    switch (sortMethod) {
      case "lastStateChangeDate":
        for (const state of this.getStateKeys()) {
          for (const specialty of this.getSpecialtyKeys(state)) {
            const all = this.getPerSpecialtyInState(state, specialty).allConsults.sort((a, b) => {
              if (!a.lastStateChangeDate) return 1;
              if (!b.lastStateChangeDate) return -1;

              return new Date(a.lastStateChangeDate).getTime() - new Date(b.lastStateChangeDate).getTime();
            });
            const owned = this.getPerSpecialtyInState(state, specialty).ownedConsults.sort((a, b) => {
              if (!a.lastStateChangeDate) return 1;
              if (!b.lastStateChangeDate) return -1;

              return new Date(a.lastStateChangeDate).getTime() - new Date(b.lastStateChangeDate).getTime();
            });
            const filtered = this.getPerSpecialtyInState(state, specialty).filteredByStringConsults?.sort((a, b) => {
              if (!a.lastStateChangeDate) return 1;
              if (!b.lastStateChangeDate) return -1;

              return new Date(a.lastStateChangeDate).getTime() - new Date(b.lastStateChangeDate).getTime();
            });

            if (!sortAscending) {
              all.reverse();
              owned.reverse();
              filtered?.reverse();
            }

            this.getPerSpecialtyInState(state, specialty).allConsults = all;
            this.getPerSpecialtyInState(state, specialty).ownedConsults = owned;
            this.getPerSpecialtyInState(state, specialty).filteredByStringConsults = filtered;
          }
        }
        break;
      case "date":
        for (const state of this.getStateKeys()) {
          for (const specialty of this.getSpecialtyKeys(state)) {
            const all = this.getPerSpecialtyInState(state, specialty).allConsults.sort((a, b) => {
              if (!a.requestDate) return 1;
              if (!b.requestDate) return -1;

              return new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime();
            });
            const owned = this.getPerSpecialtyInState(state, specialty).ownedConsults.sort((a, b) => {
              if (!a.requestDate) return 1;
              if (!b.requestDate) return -1;

              return new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime();
            });

            const filtered = this.getPerSpecialtyInState(state, specialty).filteredByStringConsults?.sort((a, b) => {
              if (!a.requestDate) return 1;
              if (!b.requestDate) return -1;

              return new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime();
            });

            if (!sortAscending) {
              all.reverse();
              owned.reverse();
              filtered?.reverse();
            }

            console.log(all, owned, filtered);

            this.getPerSpecialtyInState(state, specialty).allConsults = all;
            this.getPerSpecialtyInState(state, specialty).ownedConsults = owned;
            this.getPerSpecialtyInState(state, specialty).filteredByStringConsults = filtered;
          }
        }
        break;

      default:
        break;
    }

    this.requestTables().forEach((t) => t.reload());
  }

  private setHeaders() {
    this.energyConsultListHeaders.push({
      visualName: "COACH",
      resolver: this.displayCoach,
      sorting: {
        sort: true,
        customSortFunction(element: EnergyConsult) {
          return element.coach?.fullName.toLowerCase() ?? "zzz";
        },
      },
    });
    this.energyConsultListHeaders.push({
      visualName: "DATE",
      resolver: this.displayDate,
      sorting: {
        sort: true,
        customSortFunction(element) {
          return new Date(element.requestDate).getTime();
        },
      },
    });
    this.energyConsultListHeaders.push({
      visualName: "APPOINTMENT",
      resolver: this.displayAppointment,
      sorting: {
        sort: true,
        customSortFunction(element: EnergyConsult) {
          return element.appointmentDate ? new Date(element.appointmentDate).getTime() : "zzz";
        },
      },
    });
    this.energyConsultListHeaders.push({
      visualName: "LOCATION",
      resolver: this.displayLocation,
      sorting: {
        sort: true,
      },
    });
    this.energyConsultListHeaders.push({
      visualName: "POSTALCODE",
      resolver: this.displayPostalCode,
      sorting: {
        sort: true,
      },
    });

    if (ENVIRONMENT.MODULES.includes("NOTE")) {
      this.energyConsultListHeaders.push({
        visualName: "NOTE",
        customTemplate: this.noteColumnTemplate,
        sorting: {
          sort: false,
        },
      });
    }

    this.energyConsultListHeaders.push({
      visualName: "OPEN",
      link: { baseUrl: this.auth.roleName === "coach" ? "/content/coach/request/" : "/content/coordinator/request/", params: ["id"] },
    });
  }

  public displayState: (request: EnergyConsult) => string = (request) => {
    return this.translate.instant("COMPONENTS.TILE.STATUSES." + request.state.name);
  };

  public displaySpecialty: (request: EnergyConsult) => string = (request) => {
    return request.specialty.name;
  };

  public displayCoach: (request: EnergyConsult) => string = (request) => {
    return request.coach ? request.coach.firstName + " " + request.coach.lastName : this.translate.instant("COMPONENTS.TILE.ABSENT_DATA");
  };

  public displayDate: (request: EnergyConsult) => string = (request) => {
    let x: string = this.translate.instant("COMPONENTS.TILE.ABSENT_DATA");
    if (request.requestDate) {
      const [dateStr, timeStr] = request.requestDate.split("T");
      const date = dateStr.split("-").reverse().join("-");
      const time = timeStr.slice(0, 5);
      x = [date, time].join(" ");
    }
    if (ENVIRONMENT.MODULES.includes("DM") && this.auth.roleName === "coordinator" && request.extraProperties?.dm) {
      x += " (DM)";
    }

    return x;
  };

  public displayAppointment: (request: EnergyConsult) => string = (request) => {
    if (!request.appointmentDate) return this.translate.instant("COMPONENTS.TILE.ABSENT_DATA");

    const [dateStr, timeStr] = request.appointmentDate.split("T");
    const date = dateStr.split("-").reverse().join("-");
    const time = timeStr.slice(0, 5);
    return [date, time].join(" ");
  };

  // public displayLocation: (request: EnergyConsult) => string = (request) => {
  //   return request.requestDate ? request.requestDate.toLocaleDateString() : this.translate.instant("COMPONENTS.TILE.ABSENT_DATA");
  // };

  public displayHouseNumber: (request: EnergyConsult) => string = (request) => {
    return request.houseNumberSuffix ? String(request.houseNumber + request.houseNumberSuffix.toUpperCase()) : String(request.houseNumber);
  };
  public displayLocation: (request: EnergyConsult) => string = (request) => {
    return request.getLocation(this.application);
  };

  public displayPostalCode: (request: EnergyConsult) => string = (request) => {
    return (
      request.postalCode.substring(0, 4) + " " + request.postalCode.substring(request.postalCode.length - 2, request.postalCode.length).toUpperCase()
    );
  };

  public setFilteredByStringConsults() {
    const field = this.filterString;
    for (const stateKey of this.getStateKeys()) {
      for (const specialtyKey of this.getSpecialtyKeys(stateKey)) {
        this.value[stateKey].value[specialtyKey].filteredByStringConsults = this.getConsults(stateKey, specialtyKey, false)
          .map((consult) => consult)
          .filter((econsult) => {
            if (
              (econsult.coach?.fullName.toLowerCase().includes(field.toLowerCase()) && econsult.coach?.fullName.length >= field.length) ||
              (econsult.coach?.email.toLowerCase().includes(field.toLowerCase()) && econsult.coach?.email.length >= field.length) ||
              (econsult.resident?.fullName.toLowerCase().includes(field.toLowerCase()) && econsult.resident?.fullName.length >= field.length) ||
              (econsult.resident?.email.toLowerCase().includes(field.toLowerCase()) && econsult.resident?.email.length >= field.length) ||
              (econsult.postalCode.toLowerCase().includes(field.toLowerCase()) && econsult.postalCode.length >= field.length) ||
              ((String(econsult.houseNumber).toLowerCase() + econsult.houseNumberSuffix?.toLowerCase()).includes(field.toLowerCase()) &&
                econsult.message.length >= field.length) ||
              (econsult.houseNumberSuffix?.toLowerCase().includes(field.toLowerCase()) && econsult.houseNumberSuffix.length >= field.length) ||
              (econsult.extraProperties?.streetname?.toLowerCase().includes(field.toLowerCase()) &&
                econsult.extraProperties.streetname.length >= field.length) ||
              (econsult.extraProperties?.addressdetails?.town?.toLowerCase().includes(field.toLowerCase()) &&
                econsult.extraProperties.addressdetails.town.length >= field.length) ||
              (econsult.message.toLowerCase().includes(field.toLowerCase()) && econsult.message.length >= field.length) ||
              (econsult.extraProperties?.dm && " (dm)".includes(field.toLowerCase())) ||
              field.length == 0
            ) {
              return true;
            } else {
              return false;
            }
          });
      }
    }
  }
}
